/**
 * Resolves the workflow executionId for the current view.
 *
 * Why this exists: several components used to derive the executionId with
 * `window.location.href.split('/').pop()`. That assumes the last URL path segment is always
 * an execution id, which only holds on the detail route. On `/start-workflow?type=<t>` it
 * yielded the literal string "start-workflow?type=<t>", which then got concatenated into
 * request URLs and produced a malformed query with two '?' characters, e.g.
 *   getFileUploadInfo?fileName=a.png&executionId=start-workflow?type=2pProductLaunchNewSeller
 *
 * The executionId only exists on the detail route (see index.jsx: '/detail/:workflowId'), so
 * we match that route explicitly instead of guessing from the tail of the URL. Every other
 * route resolves to "" — meaning "no execution context" — which callers must treat as
 * "omit the parameter".
 */

// Matches the '/detail/:workflowId' route and captures the id. Anchored so it only matches a
// real detail path, and stops at the next '/' so trailing segments never leak into the id.
const DETAIL_ROUTE = /^\/detail\/([^/?#]+)/;

/**
 * Extracts the execution id from a URL-like string (a pathname, or a hash-router fragment).
 * Returns "" when the value is not a detail route.
 */
function matchDetailRoute(path) {
    if (!path) {
        return "";
    }
    const match = DETAIL_ROUTE.exec(path);
    if (!match) {
        return "";
    }
    try {
        return decodeURIComponent(match[1]);
    } catch (e) {
        // A malformed percent-escape should not break the caller; the raw segment is still
        // more useful than throwing.
        return match[1];
    }
}

/**
 * @param {string} [propExecutionId] executionId threaded down from the detail page. This is
 *        the authoritative value (the same one used for sendTaskStatus) and always wins.
 * @returns {string} the executionId, or "" when the current view has no execution context.
 */
export function resolveExecutionId(propExecutionId) {
    if (typeof propExecutionId === "string" && propExecutionId.trim() !== "") {
        return propExecutionId.trim();
    }

    try {
        // Standard BrowserRouter path, e.g. '/detail/<uuid>'.
        const fromPath = matchDetailRoute(window.location.pathname);
        if (fromPath) {
            return fromPath;
        }
        // Defensive: also support a hash-routed URL ('#/detail/<uuid>'), which the previous
        // implementation partially handled by stripping '#' characters.
        const hash = window.location.hash || "";
        return matchDetailRoute(hash.replace(/^#+/, ""));
    } catch (e) {
        return "";
    }
}

export default resolveExecutionId;
